import type { TFunction } from "i18next";
import { useU2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { type U2104OrganizationPersonalization } from "../../../../U2104Service";
import { useState } from "react";
import { U2000FormStateEnumEditMode } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { generateTemporaryId } from "../../../../../u2000Core/tools/U2000IdUtils";
import { ORGANIZATION_PERSONALIZATION_NAME_TYPES } from "../../../common/organizationPersonalizationTypes";

// Gestion des handlers pour les personnalisations d'une organisation
export interface U2104OrganizationPersonalizationHandler {
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  isDialogOpen: boolean;
  onDlgClose: (result: string) => void;
  isDeleteDialogOpen: boolean;
  onDlgDeleteClose: (result: string) => void;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U2104OrganizationPersonalization>>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U2104OrganizationPersonalization>>;
  dataChangedEvent: number;
}

// Paramètres pour les handlers des personnalisations d'une organisation
export interface U2104OrganizationPersonalizationHandlerParam {
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U2104OrganizationPersonalization>>;
  t: TFunction;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U2104OrganizationPersonalization>>;
}

// Gestion des handlers pour les personnalisations d'une organisation
export interface U2104OrganizationPersonalizationHandlerReturn {
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;

  onDlgClose: (result: string) => void;

  isDialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;

  isDeleteDialogOpen: boolean;
  onDlgDeleteClose: (result: string) => void;

  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U2104OrganizationPersonalization>>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U2104OrganizationPersonalization>>;
  dataChangedEvent: number;
}

// Implémentation des handlers pour les personnalisations d'une organisation
export function u2104OrganizationPersonalizationHandler(params: U2104OrganizationPersonalizationHandlerParam): U2104OrganizationPersonalizationHandlerReturn {
  const { gridStateHandlers, formStateHandlers } = params;
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [dataChangedEvent, setDataChangedEvent] = useState<number>(0);

  // Gestion de l'ajout d'une personnalisation d'organisation
  const onAdd = (): void => {
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    setDialogOpen(true);
    return;
  };

  // Gestion de la modification d'une personnalisation d'organisation
  const onModify = (): void => {
    const selectedRow = gridStateHandlers.getSelectedRows()[0];
    formStateHandlers.setData(selectedRow as U2104OrganizationPersonalization);
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    setDialogOpen(true);
    return;
  };

  // Gestion de la suppression d'une personnalisation d'organisation
  const onDelete = (): void => {
    setDeleteDialogOpen(true);
    return;
  };

  // Gestion de la fermeture du dialog de modification/ajout
  const onDlgClose = (result: string): void => {
    if (result === "cancel") {
      // Pour cancel, on bypasse complètement la validation et on ferme directement
      gridStateHandlers.setErrors([]);
      formStateHandlers.cancelData();
      setDialogOpen(false);
      return;
    }

    if (result === "save") {
      // Pour save, on fait la validation normale
      const businessHandlers = formStateHandlers.getBusinessHandlers();
      if (businessHandlers && businessHandlers.validateForm) {
        const errors = businessHandlers.validateForm(formStateHandlers.getData());
        formStateHandlers.setErrors(errors);

        // Si des erreurs de validation existent, ne pas fermer le dialogue
        if (Object.keys(errors).length > 0) {
          // Important: NE PAS appeler formStateHandlers.saveData() s'il y a des erreurs
          return; // Ne pas fermer le dialogue si des erreurs de validation existent
        }

        // Seulement si pas d'erreurs, on sauvegarde
        formStateHandlers.saveData();
      }
      if (formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT) {
        // important: mettre à jour la ligne dans le gridStateHandlers && mette à jour idChecked à false
        const updatedData = gridStateHandlers.getData().map((item) => (item.id === formStateHandlers.getData().id ? { ...formStateHandlers.getData(), idChecked: false } : item));
        gridStateHandlers.setData(updatedData);
        setDataChangedEvent(dataChangedEvent + 1);
      } else if (formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.ADD) {
        const newData = formStateHandlers.getData();

        // Générer un UUID temporaire pour les nouveaux enregistrements
        newData.id = generateTemporaryId();
        // Renseigner le scope pour l'affichage immédiat dans le tableau (dérivé du type)
        const typeDef = ORGANIZATION_PERSONALIZATION_NAME_TYPES.find((t) => t.value === newData.personalizationName);
        if (typeDef) {
          newData.scope = typeDef.scope;
        }
        gridStateHandlers.setData([...gridStateHandlers.getData(), newData]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        setDataChangedEvent(dataChangedEvent + 1);
      }
      formStateHandlers.afterSaveData(formStateHandlers.getData());
      setDialogOpen(false);
    }
    return;
  };

  // Gestion de la fermeture du dialog de suppression
  const onDlgDeleteClose = (result: string): void => {
    if (result === "yes") {
      const selectedRows = gridStateHandlers.getSelectedRows();
      const remainingRows = gridStateHandlers.getData().filter((row) => !selectedRows.some((selected) => selected.id === row.id));
      gridStateHandlers.setData(remainingRows);

      gridStateHandlers.setTotalRows(remainingRows.length);
      setDataChangedEvent(dataChangedEvent + 1);
    }
    setDeleteDialogOpen(false);
    return;
  };

  return { onAdd, onModify, onDelete, onDlgClose, isDialogOpen, setDialogOpen, formStateHandlers, gridStateHandlers, isDeleteDialogOpen, onDlgDeleteClose, dataChangedEvent };
}
